import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  IconButton,
  Button as MuiButton,
  Stack,
  Typography,
} from '@mui/material';
import { Add, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useFormikContext } from 'formik';

import { JobTagSelect, useCanTagJobsOf } from 'features/job-tags';
import {
  InputCheck,
  InputSelect,
  InputText,
  InputTypeahead,
} from '@farmshare/ui-components';
import {
  animalSplitTypeHelper,
  inspectionLevelHelper,
  resolveHangTimeDays,
} from '@farmshare/utils';
import { reduce, sortBy } from 'lodash';
import moment from 'moment';
import {
  EnumCustomFieldDefinitionCaptureAt,
  EnumCustomFieldDefinitionFieldType,
  EnumCustomFieldDefinitionModelType,
  EnumProcessorSchedulingAnimalSpecies,
  EnumUserType,
  useProcessorCustomerByIdQuery,
} from '../../../../lib/graphql';
import { isKillAndChillAvailable } from '../../../../lib/killAndChillUtils';
import { useCustomerOptions } from 'features/customers/hooks/use-customer-options';
import { CustomerForm } from 'features/customers';
import {
  getRequiredCustomFieldIds,
  RegistryFieldInput,
  useCustomFieldDefs,
} from 'features/custom-fields';
import { RecurrenceFields } from 'features/jobs/recurring/recurrence';
import { useUserState } from 'providers';
import { QuickScheduleForm } from './types';
import { getSplitSlotsForType, isAnimalTouched } from './utils';

// ~1/3 width on sm+ so per-animal cells (built-ins + head custom fields)
// pack three-up with the Stack's 8px gap, full-width on xs.
const ANIMAL_FIELD_CELL_SX = {
  flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 8px)' },
  minWidth: { xs: 0, sm: 200 },
} as const;

export function ReviewStep({
  vendorId,
  processorSettings = null,
  expandHeadDetailsRef = undefined,
  requesterProducers = undefined,
  showJobTags = true,
}: {
  vendorId: string;
  processorSettings?: unknown;
  /**
   * The parent wrapper's validator calls this when head-field validation
   *  fails so the accordion opens and the inline errors are visible. We pass
   *  it through a ref instead of watching `errors` because head inputs may
   *  auto-set values on mount (single-option InputSelect, etc.), which would
   *  otherwise force the accordion open on load.
   */
  expandHeadDetailsRef?: { current: (() => void) | null };
  /**
   * Every producer this review will create a job for.
   *
   * `values.requesterContactInformation` is single-valued, so the Producer
   * card below would otherwise name only ONE producer on a review that creates
   * several jobs — actively misleading, since it reads as "this is who the job
   * is for". Pass the full set and the card lists them all.
   *
   * Each entry carries a caller-supplied `name`/`company` used when the
   * customer query has not resolved (it is cache-and-network, so the first
   * renders have no options at all). Contact details are still preferred from
   * that query when it lands.
   *
   * Omit (or pass one entry) on every single-job caller; the card renders
   * exactly as it always has.
   */
  requesterProducers?: Array<{ id: string; name?: string; company?: string }>;
  /**
   * Pass false where this review does NOT create a job — the herd dialog's
   * "add to an existing job" path — so it never offers a tag picker whose
   * selection the submit has nowhere to put.
   */
  showJobTags?: boolean;
}) {
  const { values, setFieldValue } = useFormikContext<QuickScheduleForm>();
  // The job's vendor, not just "my processor has tags": the herd dialog lets
  // a producer pick WHICH processor, and the picker only ever offers the
  // active vendor's tags.
  const canTagJob = useCanTagJobsOf(vendorId);
  // Booking custom-field definitions are processor-only on the backend
  // (`customFieldDefinitionMany` → `processorRoleOnly`). This review step is
  // shared with the producer "Request a Job" wizard, where the actor is a
  // producer — so gate those queries off for non-processors to avoid a
  // "Processor access required" error (producers don't collect them here).
  const { isProcessor } = useUserState();

  // Calculate enabled animal split options from processor settings
  const enabledAnimalSplitOptions = useMemo(() => {
    const animalSetting = (
      processorSettings as {
        animalSettings?: Array<{
          species?: string;
          isEnabled?: boolean;
          splitTypes?: unknown[];
        }>;
      }
    )?.animalSettings?.find(
      (setting: { species?: string; isEnabled?: boolean }) =>
        setting?.species === values.species && setting?.isEnabled,
    );

    return sortBy(
      reduce(
        animalSetting?.splitTypes || [],
        (options, splitType) => {
          if (splitType && typeof splitType === 'string') {
            options.push(animalSplitTypeHelper(splitType));
          }
          return options;
        },
        [] as Array<{ label: string; value: string; order: number }>,
      ),
      'order',
    );
  }, [processorSettings, values.species]);
  const [showDetails, setShowDetails] = useState(false);

  // Hand the parent wrapper a way to open the Animal Details accordion when
  // its validate-and-touch handler finds missing required head fields. We do
  // this via a ref so that the open is only ever caused by a real validation
  // attempt — never by mount-time form mutations.
  useEffect(() => {
    if (!expandHeadDetailsRef) return undefined;
    expandHeadDetailsRef.current = () => setShowDetails(true);
    return () => {
      expandHeadDetailsRef.current = null;
    };
  }, [expandHeadDetailsRef]);

  const [showAddBuyer, setShowAddBuyer] = useState<Record<string, boolean>>({});

  // Reset an animal's buyer slots when its split type changes, so stale slots
  // don't linger and the picker defaults back to selecting an existing buyer.
  const prevSplitTypesRef = useRef<Array<string | undefined>>(
    values.splitTypes ?? [],
  );
  useEffect(() => {
    const prev = prevSplitTypesRef.current;
    const current = values.splitTypes ?? [];
    prevSplitTypesRef.current = current;
    current.forEach((splitType, index) => {
      if (prev[index] === splitType) return;
      setFieldValue(`splitAssignments.${index}`, {});
      setFieldValue(`splitBuyers.${index}`, {});
      setShowAddBuyer((prevState) =>
        Object.fromEntries(
          Object.entries(prevState).filter(
            ([key]) => !key.startsWith(`${index}-`),
          ),
        ),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.splitTypes, setFieldValue]);

  const canLoadProducers = Boolean(vendorId);
  // Unfiltered by relationship type on purpose: this one list backs both the
  // producer summary below and the split-slot buyer pickers further down.
  // `pinnedId` covers the producer the Select Producer step handed us — it can
  // sit outside the resolver's result cap, and without it the summary falls
  // back to the (empty, for a dropdown selection) manual contact fields.
  const { customerOptions } = useCustomerOptions(vendorId, canLoadProducers, {
    includeAddNew: false,
    pinnedId: values.requesterContactInformation.producer,
  });

  const selectedProducer = values.requesterContactInformation.producer
    ? customerOptions.find(
        (option) => option._id === values.requesterContactInformation.producer,
      )
    : null;

  // The producers this review is for — ONE entry for a normal single-job
  // review, one per job on a fan-out. Both cases render through the same list
  // below so the two presentations cannot drift apart (they already had: the
  // multi branch silently dropped the phone number the single branch showed).
  //
  // `customerOptions` is the richer source but arrives late (cache-and-network),
  // so every field falls back — to the caller-supplied name/company on a
  // fan-out, and to the form's own contact values on a single job. Nothing here
  // can render as "Unknown producer" over a button that creates the jobs.
  const isMultiProducer = Boolean(
    requesterProducers && requesterProducers.length > 1,
  );
  const producerCards = isMultiProducer
    ? requesterProducers!.map((p) => {
        const match = customerOptions.find((option) => option._id === p.id);
        return {
          key: p.id,
          name: match?.fullName || p.name || 'Producer',
          email: match?.email ?? '',
          phone: match?.phone ?? '',
          company: match?.company || p.company || '',
        };
      })
    : [
        {
          key: 'single',
          name: selectedProducer
            ? selectedProducer.fullName
            : `${values.requesterContactInformation.first_name} ${values.requesterContactInformation.last_name}`,
          email:
            selectedProducer?.email ||
            values.requesterContactInformation.email ||
            '',
          phone:
            selectedProducer?.phone ||
            values.requesterContactInformation.phone ||
            '',
          company:
            selectedProducer?.company ||
            values.requesterContactInformation.company ||
            '',
        },
      ];

  const isBeef = values.species === EnumProcessorSchedulingAnimalSpecies.Beef;

  // Kill & chill is only offered when the processor has enabled it for the
  // selected species (mirrors the gating in add-animal-head-form).
  const killAndChillAvailable = isKillAndChillAvailable(
    (
      processorSettings as {
        killAndChillSettings?: Parameters<typeof isKillAndChillAvailable>[0];
      } | null
    )?.killAndChillSettings,
    values.species,
  );

  // Booking-time custom field definitions for Job and Head. Standard-source
  // fields (e.g. the seeded `sex` field on Head/booking) are intentionally
  // dropped: their values aren't read by `createScheduling` or by the
  // booking-time bulkSave path, and the Sex case is already handled by the
  // hardcoded select below for beef. Including them would render a duplicate,
  // unpersisted input.
  const { definitions: jobBookingDefsRaw } = useCustomFieldDefs({
    vendorId,
    species: values.species,
    captureAt: EnumCustomFieldDefinitionCaptureAt.Booking,
    modelType: EnumCustomFieldDefinitionModelType.Job,
    enabled: isProcessor,
  });
  const { definitions: headBookingDefsRaw } = useCustomFieldDefs({
    vendorId,
    species: values.species,
    captureAt: EnumCustomFieldDefinitionCaptureAt.Booking,
    modelType: EnumCustomFieldDefinitionModelType.Head,
    enabled: isProcessor,
  });
  const jobBookingDefs = useMemo(
    () => jobBookingDefsRaw.filter((d) => d.fieldSource !== 'standard'),
    [jobBookingDefsRaw],
  );
  const headBookingDefs = useMemo(
    () => headBookingDefsRaw.filter((d) => d.fieldSource !== 'standard'),
    [headBookingDefsRaw],
  );

  // Toggles get their own row so they don't sit awkwardly between text/select inputs.
  const jobBookingToggleDefs = useMemo(
    () =>
      jobBookingDefs.filter(
        (d) => d.fieldType === EnumCustomFieldDefinitionFieldType.Toggle,
      ),
    [jobBookingDefs],
  );
  const jobBookingNonToggleDefs = useMemo(
    () =>
      jobBookingDefs.filter(
        (d) => d.fieldType !== EnumCustomFieldDefinitionFieldType.Toggle,
      ),
    [jobBookingDefs],
  );

  // Stash fieldType-by-id so handleSubmit can coerce Number inputs on save,
  // plus the required-id lists so the multistep `canGoNext` gate (which has no
  // access to definitions) can validate required custom fields.
  // Update only when the map content changes — writing a new map ref each
  // render churns Formik and re-fires nested effects (notably InputSelect's
  // autoSelectFirstOption), which can hit React's update-depth ceiling.
  const lastCustomFieldMetaJsonRef = useRef<string>('');
  useEffect(() => {
    const types: Record<string, EnumCustomFieldDefinitionFieldType> = {};
    for (const d of jobBookingDefs) types[d._id] = d.fieldType;
    for (const d of headBookingDefs) types[d._id] = d.fieldType;
    const requiredIds = {
      job: getRequiredCustomFieldIds(jobBookingDefs),
      head: getRequiredCustomFieldIds(headBookingDefs),
    };
    const next = JSON.stringify({ types, requiredIds });
    if (next === lastCustomFieldMetaJsonRef.current) return;
    lastCustomFieldMetaJsonRef.current = next;
    setFieldValue('_customFieldTypes', types);
    setFieldValue('_requiredCustomFieldIds', requiredIds);
  }, [jobBookingDefs, headBookingDefs, setFieldValue]);

  // Extract hang time options for this species
  const hangTimeOptions = useMemo(() => {
    const settings = (
      processorSettings as {
        hangTimeSettings?: Array<{
          species?: string;
          isEnabled?: boolean;
          options?: Array<{
            durationDays: number;
            isDefault?: boolean | null;
            upcharge?: number | null;
            availableAtBooking?: boolean | null;
          } | null> | null;
        } | null>;
      }
    )?.hangTimeSettings;

    const speciesConfig = settings?.find(
      (s) => s?.species === values.species && s?.isEnabled,
    );

    return (speciesConfig?.options ?? []).filter(
      (o): o is NonNullable<typeof o> => o != null && !!o.availableAtBooking,
    );
  }, [processorSettings, values.species]);

  // Fetch producer's hang time preferences
  const producerId = values.requesterContactInformation.producer;
  const { data: producerData } = useProcessorCustomerByIdQuery({
    variables: { id: producerId ?? '' },
    skip: !producerId,
  });

  // Pre-select producer's preferred hang time once per (producer, species).
  // Tracking the last-applied key — instead of gating on `hangTimeDays.length` —
  // means manual chip selections aren't clobbered when quantity changes, and
  // changing species/producer correctly re-pre-selects.
  const lastPreSelectedKeyRef = useRef<string | null>(null);
  const userChoseHangTimeRef = useRef(false);
  useEffect(() => {
    userChoseHangTimeRef.current = false;
    lastPreSelectedKeyRef.current = null;
  }, [producerId, values.species]);

  useEffect(() => {
    const key = `${producerId ?? ''}|${values.species}`;
    if (
      !producerData?.userById ||
      hangTimeOptions.length === 0 ||
      userChoseHangTimeRef.current ||
      lastPreSelectedKeyRef.current === key
    ) {
      return;
    }
    const { days, source } = resolveHangTimeDays({
      producerPreferences: producerData.userById.settings?.hangTimePreferences,
      vendorId,
      species: values.species,
    });
    if (source === 'producer' && days != null) {
      const allHangTimes = Array.from({ length: values.quantity }, () => days);
      setFieldValue('hangTimeDays', allHangTimes);
      lastPreSelectedKeyRef.current = key;
    }
    // `values.quantity` is intentionally NOT a dep: quantity changes shouldn't
    // re-fire the pre-select (D5 test plan). The lastPreSelectedKeyRef and
    // userChoseHangTimeRef guards above gate re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    producerData,
    hangTimeOptions.length,
    values.species,
    producerId,
    vendorId,
    setFieldValue,
  ]);

  // Initialize producer identifiers array if not present
  useEffect(() => {
    if (
      !values.producerIdentifiers ||
      values.producerIdentifiers.length === 0
    ) {
      setFieldValue(
        'producerIdentifiers',
        Array.from({ length: values.quantity }, () => ''),
      );
    }
  }, [values.quantity, values.producerIdentifiers, setFieldValue]);

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {/* Summary Card */}
      <Card variant="outlined">
        <CardHeader
          title={
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {values.quantity}x{' '}
              {values.species.charAt(0).toUpperCase() + values.species.slice(1)}
              {/* The count is the TOTAL across every job this review creates,
                not one job's head count — say so, or "2x Beef" reads as a
                single two-head job. */}
              {isMultiProducer && (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 400, ml: 1 }}
                >
                  across {producerCards.length} jobs
                </Typography>
              )}
            </Typography>
          }
          subheader={
            <Box>
              <Typography variant="body2" color="text.secondary">
                {moment(values.date).format('MMMM D, YYYY')} @{' '}
                {values.blockTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Inspection:{' '}
                {inspectionLevelHelper(values.inspectionLevel).label}
              </Typography>
            </Box>
          }
        />
        <CardContent>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            {isMultiProducer
              ? `Producers (${producerCards.length})`
              : 'Producer'}
          </Typography>
          {/* One entry per job. On a fan-out the heading is pluralised and
              counted so this block can never be read as "the producer this job
              is for". */}
          <Stack spacing={1}>
            {producerCards.map((p) => (
              <Box key={p.key}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {p.name}
                </Typography>
                {p.email && (
                  <Typography variant="body2" color="text.secondary">
                    {p.email}
                  </Typography>
                )}
                {p.phone && (
                  <Typography variant="body2" color="text.secondary">
                    {p.phone}
                  </Typography>
                )}
                {p.company && (
                  <Typography variant="body2" color="text.secondary">
                    {p.company}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Hang Time Selection + Job-level booking custom fields */}
      {(hangTimeOptions.length > 0 || jobBookingDefs.length > 0) && (
        <Card variant="outlined">
          <CardContent>
            {hangTimeOptions.length > 0 && (
              <>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Hang Time
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {hangTimeOptions.map((opt) => {
                    // Default hang time for all animals (use first animal's value or default)
                    const currentValue = values.hangTimeDays?.[0];
                    const isSelected =
                      currentValue === opt.durationDays ||
                      (!currentValue && opt.isDefault);
                    const label =
                      opt.upcharge && opt.upcharge > 0
                        ? `${opt.durationDays} days (+$${opt.upcharge})`
                        : `${opt.durationDays} days`;
                    return (
                      <Chip
                        key={opt.durationDays}
                        label={label}
                        variant={isSelected ? 'filled' : 'outlined'}
                        color={isSelected ? 'primary' : 'default'}
                        onClick={() => {
                          // Set hang time for all animals
                          const allHangTimes = Array.from(
                            { length: values.quantity },
                            () => opt.durationDays,
                          );
                          setFieldValue('hangTimeDays', allHangTimes);
                          userChoseHangTimeRef.current = true;
                        }}
                        sx={{ cursor: 'pointer' }}
                      />
                    );
                  })}
                </Stack>
              </>
            )}
            {jobBookingToggleDefs.length > 0 && (
              <Stack
                spacing={2}
                direction={{ xs: 'column', sm: 'row' }}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: hangTimeOptions.length > 0 ? 2 : 0 }}
              >
                {jobBookingToggleDefs.map((def) => (
                  <RegistryFieldInput
                    key={def._id}
                    definition={def}
                    fieldPrefix=""
                    size="small"
                  />
                ))}
              </Stack>
            )}
            {jobBookingNonToggleDefs.length > 0 && (
              <Stack
                spacing={1}
                direction={{ xs: 'column', sm: 'row' }}
                flexWrap="wrap"
                useFlexGap
                sx={{
                  mt:
                    hangTimeOptions.length > 0 ||
                    jobBookingToggleDefs.length > 0
                      ? 2
                      : 0,
                }}
              >
                {jobBookingNonToggleDefs.map((def) => (
                  <Box key={def._id} sx={{ flex: 1, minWidth: 200 }}>
                    <RegistryFieldInput
                      definition={def}
                      fieldPrefix=""
                      size="small"
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {/* Job tags — processor-only, only when the module is on, and only
          for a job at the processor's own plant. `useCanTagJobsOf` reads the
          ACTIVE VENDOR, so it is already false in the producer-facing
          wizards; the `isProcessor` check keeps that explicit alongside the
          recurring card below. */}
      {isProcessor && canTagJob && showJobTags && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Tags
            </Typography>
            <JobTagSelect
              value={values.tags ?? []}
              onChange={(tags) => setFieldValue('tags', tags)}
              species={values.species}
              label="Tags"
            />
          </CardContent>
        </Card>
      )}

      {/* Recurring Job — processor-only: this review step is shared with the
          producer "Request a Job" wizard and the schedule-from-herd dialog,
          whose submit paths have no makeRecurring/recurrence handling. */}
      {isProcessor && (
        <Card variant="outlined">
          <CardContent>
            <InputCheck
              label="Make this a recurring job"
              nameOverride="makeRecurring"
            />
            {values.makeRecurring && (
              <Box sx={{ mt: 2 }}>
                <RecurrenceFields
                  dropoffDate={values.date}
                  prefix="recurrence"
                  showKeepScheduled
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Deposits aren&apos;t collected on recurring jobs.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Optional Details Section - Per Animal */}
      <Card variant="outlined">
        <CardHeader
          title="Animal Details (Optional)"
          subheader="Provide additional details for each animal"
          onClick={() => setShowDetails(!showDetails)}
          avatar={
            <IconButton size="small" sx={{ ml: 1 }}>
              {showDetails ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          }
        />
        <Collapse in={showDetails}>
          <CardContent>
            <Stack spacing={3}>
              {Array.from({ length: values.quantity }, (_, index) => (
                <Card
                  key={index}
                  variant="outlined"
                  sx={{ bgcolor: 'grey.50' }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      Animal {index + 1}
                    </Typography>
                    {/* Per-animal fields (built-ins + head custom fields)
                        share one wrapping flex grid so widths line up at
                        ~1/3 per cell instead of head custom fields stretching
                        the full row width on their own line. */}
                    <Stack
                      spacing={1}
                      direction={{ xs: 'column', sm: 'row' }}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Box sx={ANIMAL_FIELD_CELL_SX}>
                        <InputText
                          label="Producer Identifier"
                          nameOverride={`producerIdentifiers[${index}]`}
                          size="small"
                          placeholder={`e.g., Tag #${index + 1}`}
                        />
                      </Box>
                      <Box sx={ANIMAL_FIELD_CELL_SX}>
                        <InputSelect
                          label="Split Type"
                          nameOverride={`splitTypes[${index}]`}
                          size="small"
                          options={enabledAnimalSplitOptions}
                        />
                      </Box>
                      {isBeef && (
                        <Box sx={ANIMAL_FIELD_CELL_SX}>
                          <InputSelect
                            label="Sex"
                            nameOverride={`sexes[${index}]`}
                            size="small"
                            options={[
                              { label: 'Bull', value: 'bull' },
                              { label: 'Steer', value: 'steer' },
                              { label: 'Heifer', value: 'heifer' },
                              { label: 'Cow', value: 'cow' },
                            ]}
                          />
                        </Box>
                      )}
                      {headBookingDefs.map((def) => {
                        // Until the user engages with this animal, drop the
                        // definition's `required` flag so the underlying
                        // InputText/InputSelect doesn't register a field-level
                        // validator that emits "X is required" on a section
                        // the user never opened. The top-level validator
                        // applies the same touched gate, so submission stays
                        // blocked once they do engage.
                        const effectiveDef = isAnimalTouched(values, index)
                          ? def
                          : { ...def, required: false };
                        return (
                          <Box key={def._id} sx={ANIMAL_FIELD_CELL_SX}>
                            <RegistryFieldInput
                              definition={effectiveDef}
                              fieldPrefix={`head.${index}`}
                              size="small"
                            />
                          </Box>
                        );
                      })}
                    </Stack>
                    {/* Over 30 months — left-aligned row under custom fields */}
                    {isBeef && (
                      <Box sx={{ mt: 1 }}>
                        <InputCheck
                          label="Over 30 months"
                          nameOverride={`isOverThirtyMonths[${index}]`}
                        />
                      </Box>
                    )}
                    {/* Kill & chill — own row, only when enabled for the species */}
                    {killAndChillAvailable && (
                      <Box sx={{ mt: 1 }}>
                        <InputCheck
                          label="Kill & chill"
                          nameOverride={`isKillAndChill[${index}]`}
                        />
                      </Box>
                    )}
                    {/* Split Assignments */}
                    {values.splitTypes?.[index] && (
                      <Box
                        sx={{
                          mt: 2,
                          pl: 2,
                          borderLeft: 2,
                          borderColor: 'primary.light',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                        >
                          Assign Splits to Buyers (Optional)
                        </Typography>
                        <Stack spacing={1}>
                          {getSplitSlotsForType(values.splitTypes[index]).map(
                            (slot, slotIdx) => {
                              const buyerKey = `${index}-${slotIdx}`;
                              return (
                                <Box key={slotIdx}>
                                  <InputTypeahead
                                    label={slot.label}
                                    nameOverride={`splitAssignments.${index}.${slotIdx}`}
                                    options={customerOptions}
                                    idKey="_id"
                                    labelKey="fullName"
                                    size="small"
                                    disabled={showAddBuyer[buyerKey]}
                                  />
                                  <MuiButton
                                    variant="text"
                                    startIcon={<Add />}
                                    size="small"
                                    onClick={() =>
                                      setShowAddBuyer({
                                        ...showAddBuyer,
                                        [buyerKey]: !showAddBuyer[buyerKey],
                                      })
                                    }
                                    disabled={showAddBuyer[buyerKey]}
                                    sx={{ mt: 0.5 }}
                                  >
                                    Add New Buyer
                                  </MuiButton>
                                  {showAddBuyer[buyerKey] && (
                                    <Box sx={{ mt: 1 }}>
                                      <CustomerForm
                                        vendorId={vendorId}
                                        forcedType={EnumUserType.Buyer}
                                        context="scheduling-contact"
                                        presetProducerId={
                                          values.requesterContactInformation
                                            .producer
                                        }
                                        onCancel={() =>
                                          setShowAddBuyer({
                                            ...showAddBuyer,
                                            [buyerKey]: false,
                                          })
                                        }
                                        showActions={false}
                                        prefix={`splitBuyers.${index}.${slotIdx}`}
                                        renderAsDiv
                                      />
                                    </Box>
                                  )}
                                </Box>
                              );
                            },
                          )}
                        </Stack>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Collapse>
      </Card>
    </Stack>
  );
}
